/**
 * Eric 2026-07-30 (B596 enhancement) — Ding audio discret pour les nouveaux
 * messages en Messagerie.
 *
 * Design :
 *   - Synthétisé via Web Audio API (aucun fichier .mp3/.wav à charger).
 *   - Sine wave 880 Hz (La 5), 150 ms, avec fade-in/fade-out sur les 20 ms
 *     aux bords pour éviter le clic audible.
 *   - Volume par défaut à 10 % du max (`gain 0.1`).
 *   - Préférence utilisateur persistée dans `localStorage` sous la clé
 *     `knoria_msg_sound_enabled`. Muet par défaut (`false`) — l'utilisateur
 *     doit l'activer explicitement (bouton 🔈 dans la messagerie).
 *   - B596.d (Eric 2026-07-30) : la préférence est aussi synchronisée
 *     côté serveur via PATCH /auth/me/preferences pour être partagée
 *     entre appareils (desktop/mobile).
 *   - Toute erreur (contexte audio non supporté, permission refusée…) est
 *     silencieusement swallowed pour ne jamais bloquer l'UI.
 *
 * Usage :
 *   import { playDing, isSoundEnabled, setSoundEnabled, syncSoundFromServer } from '../lib/messagingSound';
 *   if (isSoundEnabled()) playDing();
 */
import axios from "axios";
import { API_BASE as API } from "./apiBase";

const STORAGE_KEY = "knoria_msg_sound_enabled";
const SERVER_PREF_KEY = "messaging_sound_enabled";

export function isSoundEnabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setSoundEnabled(enabled) {
  try {
    if (enabled) localStorage.setItem(STORAGE_KEY, "1");
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage indisponible (mode private) — silent */
  }
  // B596.d — Sync serveur (best-effort, ne bloque jamais l'UX)
  try {
    axios.patch(
      `${API}/api/auth/me/preferences`,
      { [SERVER_PREF_KEY]: enabled ? true : null },
      { withCredentials: true }
    ).catch(() => { /* silent — préférence purement UX */ });
  } catch {
    /* silent */
  }
}

/**
 * B596.d — Applique la préférence lue depuis /auth/me côté serveur.
 * Appelé au mount d'AgoraPage / ChannelsPanel pour hydrater le state
 * initial depuis le profil (utile quand user passe d'un appareil à
 * un autre).
 * @param {object|null|undefined} serverPrefs
 * @returns {boolean} true si le state local a été mis à jour
 */
export function syncSoundFromServer(serverPrefs) {
  if (!serverPrefs || typeof serverPrefs !== "object") return false;
  const serverVal = serverPrefs[SERVER_PREF_KEY];
  if (serverVal === undefined) return false; // pas défini côté serveur, on garde local
  const localVal = isSoundEnabled();
  if (Boolean(serverVal) === localVal) return false; // déjà sync
  // Sync sans re-PATCH (server est source de vérité ici)
  try {
    if (serverVal) localStorage.setItem(STORAGE_KEY, "1");
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* silent */ }
  return true;
}

// Réutilise le même AudioContext entre les appels pour éviter la création
// répétée qui, selon le browser, peut être coûteuse et déclencher des
// warnings « too many audio contexts » en dev tools.
let sharedCtx = null;

export function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!sharedCtx) sharedCtx = new Ctx();
    // Certains navigateurs suspendent l'AudioContext tant qu'aucune
    // interaction utilisateur n'a eu lieu — .resume() est safe si déjà running.
    if (sharedCtx.state === "suspended") sharedCtx.resume();

    const now = sharedCtx.currentTime;
    const osc = sharedCtx.createOscillator();
    const gain = sharedCtx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now);
    // Enveloppe ADSR très courte pour éviter le « pop » aux bords.
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.10, now + 0.020);   // attack 20 ms
    gain.gain.setValueAtTime(0.10, now + 0.100);            // sustain
    gain.gain.linearRampToValueAtTime(0, now + 0.150);      // release 50 ms
    osc.connect(gain).connect(sharedCtx.destination);
    osc.start(now);
    osc.stop(now + 0.16);
  } catch {
    /* silent — le son ne doit jamais bloquer l'UX */
  }
}
